var DEFAULT_VOLUME = 0.25;
var SEEK_SECONDS = 10;
var VOLUME_POPUP_HIDE_DELAY = 2500;

// Some browsers (notably iOS Safari in Low Power Mode) silently refuse to
// autoplay video — even muted — until the visitor has interacted with the
// page at all. When that happens we remember the video and retry it once,
// on the visitor's very first tap/click anywhere on the page.
var pendingAutoplayVideos = [];
var globalAutoplayRetryArmed = false;

function armGlobalAutoplayRetry() {
  if (globalAutoplayRetryArmed) return;
  globalAutoplayRetryArmed = true;

  function retry() {
    pendingAutoplayVideos.forEach(function (video) {
      if (video.paused && video.dataset.wantsAutoplay === 'true') {
        var promise = video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(function () {});
        }
      }
    });
    window.removeEventListener('click', retry);
    window.removeEventListener('touchstart', retry);
  }

  window.addEventListener('click', retry);
  window.addEventListener('touchstart', retry);
}

// Videos in these sections are started by the IntersectionObserver below
// instead of by the "autoplay" attribute, so a mobile browser has no reason
// to decode anything until then — which used to leave an empty box where the
// video should be. Together with the "#t=0.001" fragment on the src, nudging
// currentTime a hair past zero makes the browser decode and paint the first
// frame, so every video shows its own still by default and no poster image
// has to be uploaded per video.
function primeFirstFrame(video) {
  if (video.getAttribute('poster')) return;

  function seekToFirstFrame() {
    if (video.currentTime !== 0) return;
    try {
      video.currentTime = 0.001;
    } catch (error) {
      // Seeking before the browser is ready — harmless, the video still plays.
    }
  }

  if (video.readyState >= 1) {
    seekToFirstFrame();
    return;
  }

  video.addEventListener('loadedmetadata', function onMetadata() {
    video.removeEventListener('loadedmetadata', onMetadata);
    seekToFirstFrame();
  });
}

function formatTime(seconds) {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  var whole = Math.floor(seconds);
  var minutes = Math.floor(whole / 60);
  var remainder = whole % 60;
  return minutes + ':' + (remainder < 10 ? '0' : '') + remainder;
}

function initCustomOrderVideoControls(root) {
  (root || document).querySelectorAll('[data-video-autoplay-wrapper]').forEach(function (wrapper) {
    if (wrapper.dataset.videoControlsInit === 'true') return;

    var video = wrapper.querySelector('video');
    if (!video) return;

    wrapper.dataset.videoControlsInit = 'true';

    primeFirstFrame(video);

    var progress = wrapper.querySelector('[data-video-seek]');
    var progressBar = wrapper.querySelector('[data-video-progress-bar]');
    var isScrubbing = false;
    var playToggle = wrapper.querySelector('[data-video-play-toggle]');
    var muteToggle = wrapper.querySelector('[data-video-mute-toggle]');
    var volumePopup = wrapper.querySelector('[data-video-volume-control]');
    var volumeSlider = wrapper.querySelector('[data-video-volume]');
    var userPaused = false;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var lastVolume = DEFAULT_VOLUME;
    var volumePopupHideTimer = null;

    video.volume = DEFAULT_VOLUME;
    pendingAutoplayVideos.push(video);

    // On desktop the popup opens on hover/focus via CSS alone. On touch
    // devices (no real hover) we open it briefly on tap so the visitor can
    // still fine-tune the volume, then auto-hide it again.
    function openVolumePopup() {
      if (!volumePopup) return;
      volumePopup.classList.add('is-open');
      clearTimeout(volumePopupHideTimer);
      volumePopupHideTimer = setTimeout(function () {
        volumePopup.classList.remove('is-open');
      }, VOLUME_POPUP_HIDE_DELAY);
    }

    function updatePlayToggle() {
      if (!playToggle) return;
      var playing = !video.paused;
      playToggle.classList.toggle('is-playing', playing);
      playToggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      playToggle.setAttribute(
        'aria-label',
        playing ? playToggle.dataset.pauseLabel : playToggle.dataset.playLabel
      );
    }

    function updateMuteToggle() {
      if (!muteToggle) return;
      var muted = video.muted || video.volume === 0;
      muteToggle.classList.toggle('is-muted', muted);
      muteToggle.setAttribute('aria-pressed', muted ? 'false' : 'true');
      muteToggle.setAttribute(
        'aria-label',
        muted ? muteToggle.dataset.unmuteLabel : muteToggle.dataset.muteLabel
      );
    }

    function updateVolumeSlider() {
      if (!volumeSlider) return;
      volumeSlider.value = video.muted ? 0 : video.volume;
    }

    var hasNegotiatedAutoplay = false;

    function play() {
      return video.play();
    }

    // The first time the video scrolls into view, start it muted — that is
    // unconditionally allowed by every browser's autoplay policy, so the
    // video is guaranteed to actually start. Only *after* that do we try to
    // add sound at the default volume; browsers honor that only if this
    // page already has permission to play audio without a fresh gesture,
    // otherwise they silently keep it muted (reflected via the
    // "volumechange" listener below, which keeps the UI in sync either
    // way). On later scrolls back into view we only resume playback and
    // never touch the mute state again, so it keeps reflecting whatever the
    // visitor (or this negotiation) last set.
    function attemptAutoplay() {
      video.dataset.wantsAutoplay = 'true';

      if (hasNegotiatedAutoplay) {
        var resume = play();
        if (resume && typeof resume.catch === 'function') {
          resume.catch(function () {});
        }
        return;
      }

      hasNegotiatedAutoplay = true;

      video.muted = true;
      video.setAttribute('muted', '');
      var promise = play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(function () {
          // Autoplay fully blocked (e.g. iOS Low Power Mode); the global
          // retry below and the visible play button both give the visitor
          // a way to start it themselves.
        });
      }

      video.muted = false;
      video.removeAttribute('muted');
      updateMuteToggle();
      updateVolumeSlider();
    }

    function seek(seconds) {
      if (!isFinite(video.duration)) {
        video.currentTime = Math.max(0, video.currentTime + seconds);
        return;
      }
      var target = video.currentTime + seconds;
      target = Math.max(0, Math.min(video.duration, target));
      video.currentTime = target;
    }

    // How far the browser has actually downloaded around the playhead, shown
    // as the dimmer fill behind the played one.
    function bufferedEnd() {
      var ranges = video.buffered;
      if (!ranges || !ranges.length) return 0;
      for (var i = 0; i < ranges.length; i++) {
        if (ranges.start(i) <= video.currentTime && ranges.end(i) >= video.currentTime) {
          return ranges.end(i);
        }
      }
      return ranges.end(ranges.length - 1);
    }

    function renderProgress() {
      if (!progress) return;

      var duration = isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      var playedRatio = duration ? video.currentTime / duration : 0;
      var bufferedRatio = duration ? bufferedEnd() / duration : 0;

      playedRatio = Math.max(0, Math.min(1, playedRatio));
      bufferedRatio = Math.max(0, Math.min(1, bufferedRatio));

      progress.style.setProperty('--video-progress', (playedRatio * 100).toFixed(2) + '%');
      progress.style.setProperty('--video-buffered', (bufferedRatio * 100).toFixed(2) + '%');
      progress.setAttribute('aria-valuenow', Math.round(playedRatio * 100));
      progress.setAttribute(
        'aria-valuetext',
        formatTime(video.currentTime) + ' / ' + formatTime(duration)
      );
    }

    // Maps a pointer's horizontal position onto the visible bar, so a tap
    // anywhere jumps straight there and a drag keeps following the finger
    // even once it leaves the bar (clamped at both ends).
    function seekToPointer(clientX) {
      if (!progressBar) return;
      if (!isFinite(video.duration) || video.duration <= 0) return;

      var rect = progressBar.getBoundingClientRect();
      if (!rect.width) return;

      var ratio = (clientX - rect.left) / rect.width;
      ratio = Math.max(0, Math.min(1, ratio));
      video.currentTime = ratio * video.duration;
      renderProgress();
    }

    if (progress) {
      progress.addEventListener('pointerdown', function (event) {
        if (event.button > 0) return;

        isScrubbing = true;
        progress.classList.add('is-scrubbing');
        if (progress.setPointerCapture) {
          try {
            progress.setPointerCapture(event.pointerId);
          } catch (error) {
            // Capture unsupported for this pointer — dragging still works
            // as long as the finger stays over the bar.
          }
        }
        seekToPointer(event.clientX);
        event.preventDefault();
      });

      progress.addEventListener('pointermove', function (event) {
        if (!isScrubbing) return;
        seekToPointer(event.clientX);
        event.preventDefault();
      });

      var endScrub = function () {
        if (!isScrubbing) return;
        isScrubbing = false;
        progress.classList.remove('is-scrubbing');
      };

      progress.addEventListener('pointerup', endScrub);
      progress.addEventListener('pointercancel', endScrub);

      progress.addEventListener('keydown', function (event) {
        var handled = true;

        switch (event.key) {
          case 'ArrowLeft':
            seek(-5);
            break;
          case 'ArrowRight':
            seek(5);
            break;
          case 'ArrowDown':
            seek(-SEEK_SECONDS);
            break;
          case 'ArrowUp':
            seek(SEEK_SECONDS);
            break;
          case 'Home':
            video.currentTime = 0;
            break;
          case 'End':
            if (isFinite(video.duration)) video.currentTime = video.duration;
            break;
          default:
            handled = false;
        }

        if (!handled) return;
        event.preventDefault();
        renderProgress();
      });

      video.addEventListener('timeupdate', renderProgress);
      video.addEventListener('durationchange', renderProgress);
      video.addEventListener('loadedmetadata', renderProgress);
      video.addEventListener('seeking', renderProgress);
      video.addEventListener('progress', renderProgress);
      renderProgress();
    }

    if (playToggle) {
      playToggle.addEventListener('click', function () {
        if (video.paused) {
          userPaused = false;
          video.dataset.wantsAutoplay = 'true';
          play();
        } else {
          userPaused = true;
          video.dataset.wantsAutoplay = 'false';
          video.pause();
        }
      });
    }

    if (muteToggle) {
      muteToggle.addEventListener('click', function () {
        if (video.muted || video.volume === 0) {
          video.muted = false;
          video.volume = lastVolume > 0 ? lastVolume : DEFAULT_VOLUME;
        } else {
          lastVolume = video.volume;
          video.muted = true;
        }
        updateMuteToggle();
        updateVolumeSlider();
        openVolumePopup();
      });
    }

    if (volumeSlider) {
      // The slider only ever sets the volume — muting/unmuting is the
      // speaker icon's job alone. Dragging down to 0 just goes silent
      // (without flipping the mute flag); dragging back up always brings
      // sound back, even if the icon was the one that muted it.
      volumeSlider.addEventListener('input', function () {
        var value = parseFloat(volumeSlider.value);
        video.volume = value;
        if (value > 0) {
          lastVolume = value;
          if (video.muted) video.muted = false;
        }
        updateMuteToggle();
        openVolumePopup();
      });
    }

    if (volumePopup) {
      document.addEventListener('click', function (event) {
        if (!volumePopup.contains(event.target)) {
          clearTimeout(volumePopupHideTimer);
          volumePopup.classList.remove('is-open');
        }
      });
    }

    video.addEventListener('play', updatePlayToggle);
    video.addEventListener('pause', updatePlayToggle);
    video.addEventListener('volumechange', function () {
      updateMuteToggle();
      updateVolumeSlider();
    });

    updatePlayToggle();
    updateMuteToggle();
    updateVolumeSlider();

    if (reducedMotion) {
      // Respect reduced-motion preference: never autoplay, but the controls
      // still work so visitors can opt in.
      return;
    }

    if (!('IntersectionObserver' in window)) {
      attemptAutoplay();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (!userPaused) attemptAutoplay();
          } else {
            video.dataset.wantsAutoplay = 'false';
            if (!video.paused) video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(wrapper);
  });
}

initCustomOrderVideoControls(document);
armGlobalAutoplayRetry();

document.addEventListener('shopify:section:load', function (event) {
  initCustomOrderVideoControls(event.target);
});
